import { useState } from 'react'
import { X, MapPin } from 'lucide-react'

// Assign a user to an organization + branch (superadmin: any org; admin: own
// org only). Managers/staff must get a branch; admins may be org-wide. The
// change goes through the assign-user function so the RLS scope in the JWT
// app_metadata moves too — the user picks it up on next refresh/sign-in.
export default function AssignBranchModal({ user, currentUser, organizations, branches, onAssign, onClose }) {
  const isSuperAdmin = currentUser.role === 'superadmin'
  const [orgId, setOrgId] = useState(user.organizationId || (isSuperAdmin ? '' : currentUser.organizationId))
  const [branchId, setBranchId] = useState(user.branchId || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const orgBranches = branches.filter((b) => b.organizationId === orgId)
  const branchRequired = user.role !== 'admin'

  async function handleSave() {
    if (!orgId) return setError('Select an organization.')
    if (branchRequired && !branchId) return setError(`${user.role === 'manager' ? 'Managers' : 'Staff'} must be assigned to a branch.`)
    setError('')
    setSaving(true)
    const result = await onAssign(user.id, { organizationId: orgId, branchId: branchId || null })
    setSaving(false)
    if (result?.success === false) return setError(result.message)
    onClose()
  }

  const selectCls = 'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none bg-white focus:ring-2 focus:ring-teal-500'

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-teal-600" />
            <h3 className="font-bold text-gray-800">Assign branch</h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-500">
            <span className="font-semibold text-gray-800">{user.name}</span>
            <span className="capitalize"> · {user.role}</span>
          </p>

          {isSuperAdmin && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Organization</label>
              <select className={selectCls} value={orgId}
                onChange={(e) => { setOrgId(e.target.value); setBranchId('') }}>
                <option value="">Select organization…</option>
                {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Branch</label>
            <select className={selectCls} value={branchId} onChange={(e) => setBranchId(e.target.value)} disabled={!orgId}>
              <option value="">{branchRequired ? 'Select branch…' : 'Org-wide (all branches)'}</option>
              {orgBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            {orgId && orgBranches.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">This organization has no branches yet.</p>
            )}
          </div>

          <p className="text-[11px] text-gray-400">
            Takes effect on their next sign-in or within about an hour (when their session token refreshes).
          </p>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} disabled={saving}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-60 transition">
            {saving ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  )
}
